import { useMutation } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";

import type { Note, SingleUserNoteResponse } from "./types";

type StoredToken = {
  AccessToken?: string;
};

export type PinUserNotePayload = {
  user_id: string;
  note_id: string;
};

// HTTP method note: the BE exposes pin/unpin as PATCH on the note resource
// (toggling the `is_pinned` attribute), not POST on a command sub-resource.
// This diverges from the typical REST convention of POST for action endpoints
// (AGENTS.md §7), but matches the published backend contract — do not change
// without coordinating with the backend team.
//
// 409 from the BE means the note is already pinned. The mutation still
// throws so callers can refetch and surface a neutral toast — no rollback
// needed because a 409 means the desired state is already reached.
const pinUserNote = async ({
  user_id,
  note_id,
}: PinUserNotePayload): Promise<Note | null> => {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString
    ? JSON.parse(tokenString)
    : null;
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken,
  );

  const response = await PrivateAdminPanelInstance.patch(
    API_ROUTES.PIN_USER_NOTE.replace("{user_id}", user_id).replace(
      "{note_id}",
      note_id,
    ),
  );

  const responseBody = response?.data as
    | Partial<SingleUserNoteResponse>
    | undefined;

  return (responseBody?.data as Note | undefined) ?? null;
};

export const usePinUserNote = () =>
  useMutation<Note | null, Error, PinUserNotePayload>({
    mutationKey: ["pinUserNote"],
    mutationFn: pinUserNote,
  });
